import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/roles.helpers';

const STATUT_MAP = {
  COMPLET:    ['green', 'Complète'],
  PARTIELLE:  ['gold',  'Partielle'],
  NON_SAISIE: ['red',   'Non saisie'],
  EN_ATTENTE: ['gray',  'En attente'],
};

const TYPE = {
  CONTROLE: 'Contrôle', DEVOIR: 'Devoir',
  COMPOSITION: 'Composition', EXAMEN_BLANC: 'Examen blanc',
};

export default function EvaluationReports() {
  const { user } = useAuth();
  const userRoles = user?.roles || [];
  const canSeeSaisie = hasPermission(userRoles, 'validationNotes') ||
    userRoles.some((r) => ['ADMIN_ECOLE', 'DIRECTEUR', 'SECRETAIRE'].includes(r));

  const [tab, setTab] = useState('pedago');

  // ── Rapport pédagogique ───────────────────────────────────
  const [classes, setClasses]   = useState([]);
  const [periods, setPeriods]   = useState([]);
  const [classId, setClassId]   = useState('');
  const [periodId, setPeriodId] = useState('');
  const [report, setReport]     = useState(null);
  const [error, setError]       = useState('');

  // ── Rapport saisie ────────────────────────────────────────
  const [saisieFilter, setSaisieFilter] = useState('');
  const [saisieData, setSaisieData]     = useState([]);
  const [saisieLoading, setSaisieLoading] = useState(false);
  const [saisieError, setSaisieError]   = useState('');
  const [expanded, setExpanded]         = useState({});

  useEffect(() => {
    api.get('/bulletins/classes').then((c) => { setClasses(c); if (c[0]) setClassId(c[0].id); }).catch((e) => setError(e.message));
    api.get('/bulletins/periods').then((p) => { setPeriods(p); if (p[0]) setPeriodId(p[0].id); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (classId && periodId) {
      api.get(`/evaluations/report?classId=${classId}&periodId=${periodId}`).then(setReport).catch((e) => setError(e.message));
    }
  }, [classId, periodId]);

  useEffect(() => {
    if (tab !== 'saisie' || !canSeeSaisie) return;
    setSaisieLoading(true); setSaisieError('');
    const q = saisieFilter ? `?periodId=${saisieFilter}` : '';
    api.get(`/evaluations/rapport-saisie${q}`)
      .then(setSaisieData)
      .catch((e) => setSaisieError(e.message))
      .finally(() => setSaisieLoading(false));
  }, [tab, saisieFilter]);

  function color(rate) { return rate == null ? 'gray' : rate >= 70 ? 'green' : rate >= 50 ? 'gold' : 'red'; }

  function toggleExpand(id) { setExpanded((e) => ({ ...e, [id]: !e[id] })); }

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>Rapports pédagogiques</h2>
      </div>

      <div className="tabs" style={{ paddingLeft: 16 }}>
        <button className={`tab ${tab === 'pedago' ? 'active' : ''}`} onClick={() => setTab('pedago')}>
          Rapport par classe
        </button>
        {canSeeSaisie && (
          <button className={`tab ${tab === 'saisie' ? 'active' : ''}`} onClick={() => setTab('saisie')}>
            Suivi des saisies enseignants
          </button>
        )}
      </div>

      {/* ── Onglet rapport pédagogique ── */}
      {tab === 'pedago' && (
        <div className="panel-body">
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <select className="search-input" style={{ width: 130 }} value={classId} onChange={(e) => setClassId(e.target.value)}>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select className="search-input" style={{ width: 150 }} value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
              {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          {error && <div className="form-error">{error}</div>}
          {report && (
            <>
              <div className="kpi-grid" style={{ marginBottom: 14 }}>
                <div className="kpi navy"><span>Classe</span><strong style={{ fontSize: '1.4rem' }}>{report.class || '—'}</strong></div>
                <div className="kpi"><span>Effectif</span><strong>{report.effectif}</strong></div>
                <div className="kpi gold"><span>Moyenne de classe</span><strong>{report.classAverage ?? '—'}</strong></div>
                <div className="kpi"><span>Moyenne de passage</span><strong>{report.passMark}</strong></div>
              </div>
              {report.subjects.length === 0 ? <div className="empty">Aucune note publiée pour cette période.</div> : (
                <div className="table-wrap"><table>
                  <thead><tr>
                    <th>Matière</th><th className="num">Noté</th><th className="num">Moyenne</th>
                    <th className="num">Min</th><th className="num">Max</th><th className="num">Taux de réussite</th>
                  </tr></thead>
                  <tbody>{report.subjects.map((s) => (
                    <tr key={s.subjectId}>
                      <td>{s.name}</td>
                      <td className="num">{s.noted}</td>
                      <td className="num"><b>{s.moyenne ?? '—'}</b></td>
                      <td className="num">{s.min ?? '—'}</td>
                      <td className="num">{s.max ?? '—'}</td>
                      <td className="num">{s.successRate == null ? '—' : <span className={`badge ${color(s.successRate)}`}>{s.successRate}%</span>}</td>
                    </tr>
                  ))}</tbody>
                </table></div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Onglet suivi saisies ── */}
      {tab === 'saisie' && canSeeSaisie && (
        <div className="panel-body">
          {/* Filtre période */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: 'var(--muted)' }}>Période :</label>
            <select className="search-input" style={{ width: 180 }} value={saisieFilter} onChange={(e) => setSaisieFilter(e.target.value)}>
              <option value="">Toutes les périodes</option>
              {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {saisieError && <div className="form-error">{saisieError}</div>}
          {saisieLoading ? <div className="loading">Chargement…</div> :
           saisieData.length === 0 ? <div className="empty">Aucune donnée disponible.</div> : (
            <>
              {/* KPIs globaux */}
              {(() => {
                const totalEvals = saisieData.reduce((s, t) => s + t.total, 0);
                const totalComplets = saisieData.reduce((s, t) => s + t.complets, 0);
                const totalNonSaisies = saisieData.reduce((s, t) => s + t.nonSaisies, 0);
                const tauxGlobal = totalEvals > 0 ? Math.round((totalComplets / totalEvals) * 100) : 0;
                return (
                  <div className="kpi-grid" style={{ marginBottom: 20 }}>
                    <div className="kpi"><span>Enseignants</span><strong>{saisieData.length}</strong></div>
                    <div className="kpi"><span>Évaluations totales</span><strong>{totalEvals}</strong></div>
                    <div className={`kpi ${tauxGlobal >= 80 ? 'green' : tauxGlobal >= 50 ? 'gold' : ''}`}>
                      <span>Taux de complétion global</span><strong>{tauxGlobal}%</strong>
                    </div>
                    <div className="kpi" style={{ background: totalNonSaisies > 0 ? 'var(--danger-soft,#fff5f5)' : '' }}>
                      <span>Non saisies</span>
                      <strong style={{ color: totalNonSaisies > 0 ? 'var(--danger)' : 'inherit' }}>{totalNonSaisies}</strong>
                    </div>
                  </div>
                );
              })()}

              {/* Tableau par enseignant */}
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Enseignant</th>
                      <th className="num">Évaluations</th>
                      <th className="num">Complètes</th>
                      <th className="num">Non saisies</th>
                      <th style={{ width: 180 }}>Taux de complétion</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {saisieData.map((row) => (
                      <>
                        <tr key={row.teacher.id} style={{ cursor: 'pointer' }} onClick={() => toggleExpand(row.teacher.id)}>
                          <td>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>
                              {row.teacher.lastName} {row.teacher.firstName}
                            </div>
                          </td>
                          <td className="num">{row.total}</td>
                          <td className="num" style={{ color: 'var(--success)', fontWeight: 700 }}>{row.complets}</td>
                          <td className="num">
                            {row.nonSaisies > 0
                              ? <span className="badge red">{row.nonSaisies}</span>
                              : <span style={{ color: 'var(--muted)' }}>0</span>}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ flex: 1, height: 8, background: 'var(--line-soft)', borderRadius: 999, overflow: 'hidden' }}>
                                <div style={{
                                  height: '100%', width: `${row.taux}%`, borderRadius: 999,
                                  background: row.taux >= 80 ? 'var(--success)' : row.taux >= 50 ? 'var(--warn)' : 'var(--danger)',
                                }} />
                              </div>
                              <span style={{ fontSize: 11, minWidth: 32, fontWeight: 600 }}>{row.taux}%</span>
                            </div>
                          </td>
                          <td style={{ textAlign: 'right', fontSize: 11, color: 'var(--muted)' }}>
                            {expanded[row.teacher.id] ? '▲' : '▼'}
                          </td>
                        </tr>

                        {/* Détail des évaluations de cet enseignant */}
                        {expanded[row.teacher.id] && row.evaluations.map((ev) => {
                          const [badgeCls, badgeLabel] = STATUT_MAP[ev.statut] || ['gray', ev.statut];
                          return (
                            <tr key={ev.id} style={{ background: 'var(--surface-soft)', fontSize: 12 }}>
                              <td style={{ paddingLeft: 32, color: 'var(--muted)' }}>
                                <span style={{ fontWeight: 500, color: 'var(--ink)' }}>{ev.title}</span>
                                <span style={{ marginLeft: 6 }} className="badge gray">{TYPE[ev.type] || ev.type}</span>
                                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                                  {ev.class} · {ev.subject} · {ev.period || '—'}
                                  {ev.date && <> · {new Date(ev.date).toLocaleDateString('fr-FR')}</>}
                                </div>
                              </td>
                              <td className="num" style={{ color: 'var(--muted)' }}>{ev.effectif} élèves</td>
                              <td className="num">{ev.saisies}/{ev.effectif}</td>
                              <td className="num">—</td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <div style={{ flex: 1, height: 6, background: 'var(--line-soft)', borderRadius: 999, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${ev.rate}%`, borderRadius: 999, background: ev.rate === 100 ? 'var(--success)' : ev.rate > 0 ? 'var(--warn)' : 'var(--danger)' }} />
                                  </div>
                                  <span style={{ fontSize: 10, minWidth: 28 }}>{ev.rate}%</span>
                                </div>
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <span className={`badge ${badgeCls}`} style={{ fontSize: 10 }}>{badgeLabel}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
